import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  getDoc
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../../firebaseConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Swal from 'sweetalert2';

import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import UserNav from '../../components/navbar/user-nav';
import PhotoUpload from '../../components/PhotoUpload';
import Breadcrumb from '../../components/Breadcrumb';

// Función para obtener el schema según el modo (crear o editar)
const getSchema = (isEditMode: boolean) => yup.object().shape({
  nombre: yup.string().required('Nombre requerido'),
  correo: yup.string().email('Correo inválido').required('Correo requerido'),
  contrasena: isEditMode
    ? yup.string().optional()
    : yup.string().min(6, 'Mínimo 6 caracteres').required('Contraseña requerida'),
  confirmarContrasena: isEditMode
    ? yup.string().optional()
    : yup.string()
        .oneOf([yup.ref('contrasena')], 'Las contraseñas no coinciden')
        .required('Confirmación requerida'),
  telefono: yup.string().required('Teléfono requerido'),
  anos_experiencia: yup.number().typeError('Debe ser un número').min(0, 'Mínimo 0 años').required('Años de experiencia requerido'),
  biografia: yup.string().required('Descripción requerida'),
  direccion: yup.string().url('Debe ser una URL válida').required('Dirección requerida'),
  inmobiliaria_id: yup.string().required('Inmobiliaria requerida'),
  redes: yup.object().shape({
    facebook: yup.string().url('URL de Facebook inválida').optional(),
    instagram: yup.string().url('URL de Instagram inválida').optional()
  }),
  foto: yup.string().optional()
});

export default function AddAsociado() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editId = searchParams.get('edit');
  const isEditMode = !!editId;

  const [inmobiliarias, setInmobiliarias] = useState<{ id: string; nombre: string }[]>([]);
  const [fotoUrl, setFotoUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [contrasena, setContrasena] = useState('');
  const [confirmarContrasena, setConfirmarContrasena] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(getSchema(isEditMode))
  });

  useEffect(() => {
    const fetchData = async () => {
      // Cargar inmobiliarias
      const snapshot = await getDocs(collection(db, 'inmobiliaria'));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        nombre: doc.data().nombre
      }));
      setInmobiliarias(data);

      // Si estamos en modo edición, cargar datos del asociado
      if (isEditMode && editId) {
        setLoading(true);
        try {
          const asociadoDoc = await getDoc(doc(db, 'asociado', editId));
          if (asociadoDoc.exists()) {
            const asociadoData = asociadoDoc.data();

            // Llenar el formulario con los datos existentes
            setValue('nombre', asociadoData.nombre || '');
            setValue('correo', asociadoData.correo || '');
            setValue('telefono', asociadoData.telefono || '');
            setValue('anos_experiencia', asociadoData.años_experiencia || 0);
            setValue('biografia', asociadoData.biografia || '');
            setValue('direccion', asociadoData.direccion || '');
            setValue('inmobiliaria_id', asociadoData.inmobiliaria_id || '');
            setValue('redes.facebook', asociadoData.redes?.facebook || '');
            setValue('redes.instagram', asociadoData.redes?.instagram || '');
            setFotoUrl(asociadoData.foto || '');

            // En modo edición, la contraseña no es requerida
            setValue('contrasena', '');
            setValue('confirmarContrasena', '');
          }
        } catch (error) {
          console.error('Error cargando asociado:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [editId, isEditMode, setValue]);

  const onError = () => {
    Swal.fire({
      icon: 'warning',
      title: 'Campos incompletos',
      text: 'Completa todos los campos con asterisco (*)',
      confirmButtonColor: '#3085d6'
    });
  };

  const getFirebaseErrorMessage = (errorCode: string): string => {
    const errorMessages: { [key: string]: string } = {
      'auth/email-already-in-use': 'El correo electrónico ya está registrado en el sistema.',
      'auth/invalid-email': 'El correo electrónico no es válido.',
      'auth/operation-not-allowed': 'Operación no permitida. Contacta al administrador.',
      'auth/weak-password': 'La contraseña es demasiado débil. Debe tener al menos 6 caracteres.',
      'auth/missing-email': 'Por favor ingresa un correo electrónico.',
      'auth/missing-password': 'Por favor ingresa una contraseña.',
      'auth/invalid-credential': 'Las credenciales proporcionadas no son válidas.',
      'auth/user-disabled': 'Esta cuenta ha sido deshabilitada.',
      'auth/user-not-found': 'No existe una cuenta con este correo electrónico.',
      'auth/wrong-password': 'La contraseña es incorrecta.',
      'auth/too-many-requests': 'Demasiados intentos fallidos. Por favor intenta más tarde.',
      'auth/network-request-failed': 'Error de conexión. Verifica tu conexión a internet.',
      'permission-denied': 'No tienes permisos para realizar esta acción.',
      'unavailable': 'El servicio no está disponible temporalmente. Intenta más tarde.',
    };

    return errorMessages[errorCode] || 'Ocurrió un error inesperado. Por favor intenta nuevamente.';
  };

  const generarID = (nombre: string, inmobiliaria: string, años: number) => {
    const nombres = nombre.trim().split(/\s+/).slice(0, 3).map(n => n.slice(0, 5).toLowerCase());
    const iniciales = nombres.map(n => n[0]).join('');
    const inicialInm = inmobiliarias.find(i => i.id === inmobiliaria)?.nombre[0].toLowerCase() || 'x';
    const añosStr = años.toString().padStart(2, '0');
    const base = (iniciales + inicialInm + añosStr).slice(0, 8);
    const random = Math.floor(100 + Math.random() * 900).toString();
    return base.length < 8 ? (base + random).slice(0, 8) : base;
  };

  const onSubmit = async (data: any) => {
    try {
      if (isEditMode && editId) {
        // Modo edición - actualizar asociado existente
        await updateDoc(doc(db, 'asociado', editId), {
          nombre: data.nombre,
          correo: data.correo,
          telefono: data.telefono,
          años_experiencia: data.anos_experiencia,
          biografia: data.biografia,
          direccion: data.direccion,
          inmobiliaria_id: data.inmobiliaria_id,
          redes: data.redes,
          foto: fotoUrl || '',
          activo: true
        });

        Swal.fire({
          icon: 'success',
          title: '¡Éxito!',
          text: 'Asociado actualizado correctamente',
          confirmButtonColor: '#3085d6'
        }).then(() => {
          window.location.href = '/admin-asociado';
        });
        return;
      }

      // Modo creación - verificar si ya existe
      const snapshot = await getDocs(collection(db, 'asociado'));
      const existeCorreo = snapshot.docs.some(doc => doc.data().correo === data.correo);
      if (existeCorreo) {
        Swal.fire({
          icon: 'error',
          title: 'Correo duplicado',
          text: 'Ya existe un asociado registrado con este correo.',
          confirmButtonColor: '#d33'
        });
        return;
      }

      // Validar que la contraseña existe en modo creación
      if (!data.contrasena) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'La contraseña es requerida',
          confirmButtonColor: '#d33'
        });
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, data.correo, data.contrasena);
      const user = userCredential.user;

      const asociado_id = generarID(data.nombre, data.inmobiliaria_id, data.anos_experiencia);

      const inmobiliariaDoc = await getDoc(doc(db, 'inmobiliaria', data.inmobiliaria_id));
if (!inmobiliariaDoc.exists()) {
  Swal.fire({
    icon: 'error',
    title: 'Inmobiliaria no encontrada',
    text: 'No se encontró la inmobiliaria seleccionada.',
    confirmButtonColor: '#d33'
  });
  return;
}
const inmobiliariaData = inmobiliariaDoc.data();
const inmobiliariaIDPersonalizado = inmobiliariaData?.inmobiliaria_id;

const asociadoData = {
  asociado_id,
  tipo: 'asociado',
  nombre: data.nombre,
  correo: data.correo,
  telefono: data.telefono,
  anos_experiencia: data.anos_experiencia,
  biografia: data.biografia,
  direccion: data.direccion,
  inmobiliaria_doc_id: data.inmobiliaria_id, // Usar doc.id de Firestore (string singular)
  redes: data.redes,
  foto: fotoUrl || '',
  certificaciones: [],
  calificacion: 0,
  activo: true
};


      await addDoc(collection(db, 'asociado'), asociadoData);

      const inmobiliariaRef = doc(db, 'inmobiliaria', data.inmobiliaria_id);
      const snap = await getDoc(inmobiliariaRef);
      if (snap.exists()) {
        const existingAsociados = snap.data().asociado_id || [];
        const actualizado = Array.from(new Set([...existingAsociados, asociado_id]));
        await updateDoc(inmobiliariaRef, {
          asociado_id: actualizado
        });
      }

      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'Asociado registrado con éxito',
        confirmButtonColor: '#3085d6'
      });
      reset();
      setFotoUrl('');
    } catch (error: any) {
      console.error('Error al registrar asociado:', error);
      const errorMessage = error.code ? getFirebaseErrorMessage(error.code) : error.message;
      Swal.fire({
        icon: 'error',
        title: 'Error al registrar',
        text: errorMessage,
        confirmButtonColor: '#d33'
      });
    }
  };

  return (
    <>
      <UserNav />

      <div className="page-title">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 col-md-12">
              <h2 className="ipt-title">{isEditMode ? 'Editar Asociado' : 'Registrar Asociado'}</h2>
              <span className="ipn-subtitle">
                {isEditMode
                  ? 'Actualiza la información del asociado.'
                  : 'Completa el formulario para agregar un nuevo asociado al sistema.'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5">
        <div className="container">
          <Breadcrumb
            items={[
              { label: 'Dashboard', path: '/admin-dashboard' },
              { label: 'Administración de Asociados', path: '/admin-asociado' },
              { label: isEditMode ? 'Editar Asociado' : 'Registrar Asociado' }
            ]}
          />
          <div className="row justify-content-center">
            <div className="col-xl-10 col-lg-11 col-md-12">
              <div className="card border-0 rounded-4 p-4 shadow-sm">
                <form onSubmit={handleSubmit(onSubmit, onError)} className="taller-form-modern">
                  {/* Sección 1: Datos Básicos */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">1. Datos Básicos</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-6">
                    <label>Nombre <span className="text-danger">*</span></label>
                    <input {...register('nombre')} className="form-control" placeholder="Ej. Juan Pérez" />
                    <small className="text-danger">{String(errors.nombre?.message || '')}</small>
                  </div>

                  <div className="form-group col-md-6">
                    <label>Correo <span className="text-danger">*</span></label>
                    <input {...register('correo')} type="email" className="form-control" placeholder="correo@dominio.com" />
                    <small className="text-danger">{String(errors.correo?.message || '')}</small>
                  </div>

                  {!isEditMode && (
                    <>
                      <div className="form-group col-md-6">
                        <label>Contraseña <span className="text-danger">*</span></label>
                        <div className="position-relative">
                          <input
                            {...register('contrasena')}
                            type={showPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="******"
                            onChange={(e) => setContrasena(e.target.value)}
                            style={{ paddingRight: '45px' }}
                          />
                          <button
                            type="button"
                            className="btn position-absolute border-0"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              right: '5px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              padding: '0.375rem 0.75rem',
                              zIndex: 10,
                              background: 'transparent'
                            }}
                            title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </button>
                        </div>
                        <small className="text-danger">{String(errors.contrasena?.message || '')}</small>
                      </div>

                      <div className="form-group col-md-6">
                        <label>Confirmar contraseña <span className="text-danger">*</span></label>
                        <div className="position-relative">
                          <input
                            {...register('confirmarContrasena')}
                            type={showConfirmPassword ? "text" : "password"}
                            className="form-control"
                            placeholder="******"
                            onChange={(e) => setConfirmarContrasena(e.target.value)}
                            style={{ paddingRight: '45px' }}
                          />
                          <button
                            type="button"
                            className="btn position-absolute border-0"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{
                              right: '5px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              padding: '0.375rem 0.75rem',
                              zIndex: 10,
                              background: 'transparent'
                            }}
                            title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                          >
                            <i className={`fa ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} text-muted`}></i>
                          </button>
                        </div>
                        {confirmarContrasena && (
                          <div className="mt-1">
                            {contrasena === confirmarContrasena ? (
                              <small className="text-success">
                                ✅ Las contraseñas coinciden
                              </small>
                            ) : (
                              <small className="text-danger">
                                ❌ Las contraseñas no coinciden
                              </small>
                            )}
                          </div>
                        )}
                        <small className="text-danger">{String(errors.confirmarContrasena?.message || '')}</small>
                      </div>
                    </>
                  )}

                  <div className="form-group col-md-6">
                    <label>Teléfono <span className="text-danger">*</span></label>
                    <input {...register('telefono')} className="form-control" placeholder="7711234567" />
                    <small className="text-danger">{String(errors.telefono?.message || '')}</small>
                  </div>

                  <div className="form-group col-md-6">
                    <label>Años de experiencia <span className="text-danger">*</span></label>
                    <input {...register('anos_experiencia')} type="number" className="form-control" placeholder="Ej. 5" />
                    <small className="text-danger">{String(errors.anos_experiencia?.message || '')}</small>
                  </div>

                  <div className="form-group col-md-12">
                    <label>Descripción <span className="text-danger">*</span></label>
                    <textarea {...register('biografia')} className="form-control h-120" placeholder="Perfil profesional del asociado" />
                    <small className="text-danger">{String(errors.biografia?.message || '')}</small>
                  </div>

                  <div className="form-group col-md-12">
                    <label>Dirección (URL) <span className="text-danger">*</span></label>
                    <input {...register('direccion')} className="form-control" placeholder="https://maps.google.com/..." />
                    <small className="text-danger">{String(errors.direccion?.message || '')}</small>
                  </div>

                  <div className="form-group col-md-12">
                    <label>Inmobiliaria a la que se afilia <span className="text-danger">*</span></label>
                    <select {...register('inmobiliaria_id')} className="form-control">
                      <option value="">-- Selecciona una --</option>
                      {inmobiliarias.map((inm) => (
                        <option key={inm.id} value={inm.id}>
                          {inm.nombre}
                        </option>
                      ))}
                    </select>
                    <small className="text-danger">{String(errors.inmobiliaria_id?.message || '')}</small>
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 2: Foto de Perfil */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">2. Foto de Perfil</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-12">
                    <label>Foto de perfil del asociado</label>
                    <PhotoUpload
                      onUpload={(urls: string[]) => {
                        if (urls.length > 0) {
                          setFotoUrl(urls[0]);
                        }
                      }}
                      maxFiles={1}
                      storageFolder="asociados/perfil"
                      allowedTypes={['image/jpeg', 'image/png', 'image/webp']}
                      maxSizeMB={5}
                      className="associate-profile-upload"
                    />
                    {fotoUrl && (
                      <div className="mt-3">
                        <img 
                          src={fotoUrl} 
                          alt="Vista previa" 
                          className="img-thumbnail"
                          style={{ maxWidth: '150px', maxHeight: '150px' }}
                        />
                      </div>
                    )}
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección 3: Redes Sociales */}
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">3. Redes Sociales</h5>
                    </div>
                    <div className="card-body">
                      <div className="row">
                  <div className="form-group col-md-6">
                    <label>Facebook</label>
                    <input {...register('redes.facebook')} className="form-control" placeholder="https://facebook.com/..." />
                    <small className="text-danger">{String((errors.redes as any)?.facebook?.message || '')}</small>
                  </div>
                  <div className="form-group col-md-6">
                    <label>Instagram</label>
                    <input {...register('redes.instagram')} className="form-control" placeholder="https://instagram.com/..." />
                    <small className="text-danger">{String((errors.redes as any)?.instagram?.message || '')}</small>
                  </div>
                      </div>
                    </div>
                  </div>

                  {/* Botón de submit */}
                  <div className="text-center mt-4">
                    <button type="submit" className="btn btn-primary px-5">
                      <i className="fa fa-user-plus me-2"></i>
                      {isEditMode ? 'Actualizar Asociado' : 'Registrar Asociado'}
                      <i className="fa-solid fa-arrow-right-long ms-2"></i>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
